import React, { useState, useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  FileUp, FileSpreadsheet, Loader2, CheckCircle, XCircle, AlertCircle,
  ArrowRight, ArrowLeft, Upload, RefreshCw,
} from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { csvImportAPI } from '../services/api';

// Flexible CSV / XLSX import wizard. Three steps:
//   1. Upload — drag-and-drop a file. Server analyses columns and
//      returns an auto-suggested mapping (Your column → Our field).
//   2. Map — operator confirms / adjusts the mapping per column and
//      decides whether matching leads should be updated (upsert) or
//      skipped. Sample rows preview the mapped values.
//   3. Results — created / updated / skipped counters plus a list of
//      per-row errors. Reset to start over.
//
// The flow replaces the old business-card-shaped import where field
// names ("Business Name", "Founder Email") were hard-coded. Any
// export shape works now as long as the operator points columns at
// the right targets.

// Every importable target field gets one entry here. Order drives
// the dropdown display order in the mapping table. `label` is what
// the operator sees; `hint` shows a small subtext to disambiguate
// similarly-named fields ("Full name → splits into first + last").
const TARGET_FIELDS = [
  { value: '__skip__', label: '— Skip this column —' },
  { group: 'Identity' },
  { value: 'fullName', label: 'Full name', hint: 'Splits into First / Last' },
  { value: 'firstName', label: 'First name' },
  { value: 'lastName', label: 'Last name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'phone2', label: 'Phone 2 / WhatsApp' },
  { group: 'Address' },
  { value: 'company', label: 'Company / Business name' },
  { value: 'website', label: 'Website' },
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State / Province' },
  { value: 'country', label: 'Country' },
  { value: 'postalCode', label: 'Postal / ZIP code' },
  { group: 'Lead meta' },
  { value: 'source', label: 'Source / Channel' },
  { value: 'assignedToByName', label: 'Owner', hint: 'Matched to org user by name' },
  { value: 'stageByName', label: 'Pipeline stage', hint: 'Matched to stage by name' },
  { value: 'priority', label: 'Priority' },
  { value: 'status', label: 'Status' },
  { value: 'tags', label: 'Tags / Labels', hint: 'Comma / pipe separated' },
  { value: 'estimatedValue', label: 'Estimated value' },
  { value: 'createdAt', label: 'Created date', hint: 'Preserves history' },
  { value: 'notes', label: 'Notes / Description' },
];

const TARGET_VALUES = TARGET_FIELDS.filter((f) => f.value && f.value !== '__skip__').map((f) => f.value);

const CSVImport = () => {
  const [step, setStep] = useState('upload'); // 'upload' | 'mapping' | 'done'
  const [file, setFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [mapping, setMapping] = useState({});
  const [updateExisting, setUpdateExisting] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [results, setResults] = useState(null);

  const onDrop = useCallback(async (acceptedFiles) => {
    const selected = acceptedFiles[0];
    if (!selected) return;
    setFile(selected);
    setResults(null);
    setIsAnalyzing(true);
    try {
      const { data } = await csvImportAPI.analyze(selected);
      setAnalysis(data);
      setMapping(data.suggestedMapping || {});
      setStep('mapping');
    } catch (err) {
      console.error('Analyze error:', err);
      toast.error(err?.response?.data?.error || 'Could not analyse file');
      setFile(null);
    } finally {
      setIsAnalyzing(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
  });

  // Map of {targetField → list of source cols pointing at it} for
  // duplicate-mapping warnings (two columns mapped to phone, etc.).
  const targetUsage = useMemo(() => {
    const out = {};
    for (const [src, tgt] of Object.entries(mapping)) {
      if (!tgt || tgt === '__skip__') continue;
      out[tgt] = out[tgt] || [];
      out[tgt].push(src);
    }
    return out;
  }, [mapping]);

  const mappedSummary = useMemo(() => {
    const total = Object.keys(mapping).length;
    const skipped = Object.values(mapping).filter((v) => v === '__skip__').length;
    return { mapped: total - skipped, skipped, total };
  }, [mapping]);

  const hasIdentity = useMemo(() => {
    const used = new Set(Object.values(mapping));
    return used.has('fullName') || used.has('firstName') || used.has('email') || used.has('phone');
  }, [mapping]);

  const handleImport = async () => {
    if (!file || !mapping) return;
    if (!hasIdentity) {
      toast.error('Map at least one identifying field (Name, Email, or Phone)');
      return;
    }
    setIsImporting(true);
    try {
      const { data } = await csvImportAPI.importMapped(file, mapping, updateExisting);
      setResults(data);
      setStep('done');
      const msg = `${data.created} created · ${data.updated} updated · ${data.skipped} skipped`;
      data.errors?.length ? toast(msg, { icon: '⚠️', duration: 5000 }) : toast.success(msg);
    } catch (err) {
      console.error('Import error:', err);
      toast.error(err?.response?.data?.error || 'Import failed');
    } finally {
      setIsImporting(false);
    }
  };

  const reset = () => {
    setStep('upload');
    setFile(null);
    setAnalysis(null);
    setMapping({});
    setResults(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Lead Import</h1>
          <p className="mt-1 text-sm text-gray-600">
            Drop in a CSV / XLSX export from anywhere — we auto-detect the columns, you adjust if needed, then run.
          </p>
        </div>

        {/* Step indicator */}
        <div className="mb-6 flex items-center text-xs font-medium text-gray-500">
          <StepDot active={step === 'upload'} done={step !== 'upload'} label="1. Upload" />
          <div className="flex-1 h-px bg-gray-200 mx-2" />
          <StepDot active={step === 'mapping'} done={step === 'done'} label="2. Map columns" />
          <div className="flex-1 h-px bg-gray-200 mx-2" />
          <StepDot active={step === 'done'} label="3. Results" />
        </div>

        {/* Step 1 — Upload */}
        {step === 'upload' && (
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${
              isDragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
            }`}
          >
            <input {...getInputProps()} />
            {isAnalyzing ? (
              <div className="flex flex-col items-center">
                <Loader2 className="h-12 w-12 text-primary-500 animate-spin" />
                <p className="mt-4 text-gray-600">Analysing file…</p>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <FileUp className="h-12 w-12 text-gray-400" />
                <p className="mt-4 font-medium text-gray-900">
                  {isDragActive ? 'Drop the file here' : 'Drag & drop a CSV or XLSX file'}
                </p>
                <p className="mt-2 text-sm text-gray-500">or click to browse · max 10 MB</p>
              </div>
            )}
          </div>
        )}

        {/* Step 2 — Mapping */}
        {step === 'mapping' && analysis && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <FileSpreadsheet size={16} className="text-emerald-600" />
                    {file?.name}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {analysis.totalRows} rows · {mappedSummary.mapped} mapped · {mappedSummary.skipped} skipped
                  </div>
                </div>
                <button onClick={reset} className="text-xs text-gray-500 hover:text-gray-900 inline-flex items-center gap-1">
                  <ArrowLeft size={12} /> Upload different file
                </button>
              </div>

              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-2">Your column</th>
                    <th className="text-left px-4 py-2 w-64">Our field</th>
                    <th className="text-left px-4 py-2">Sample values</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {analysis.columns?.map((col) => {
                    const current = mapping[col] || '__skip__';
                    const usage = targetUsage[current] || [];
                    const duplicate = current !== '__skip__' && usage.length > 1;
                    const samples = analysis.sampleRows
                      ?.map((r) => String(r?.[col] ?? '').trim())
                      .filter(Boolean)
                      .slice(0, 3)
                      .join(' · ') || <span className="text-gray-400">— empty —</span>;
                    return (
                      <tr key={col} className={duplicate ? 'bg-amber-50/50' : ''}>
                        <td className="px-4 py-2.5 text-gray-900 font-medium">{col}</td>
                        <td className="px-4 py-2.5">
                          <select
                            value={current}
                            onChange={(e) => setMapping((m) => ({ ...m, [col]: e.target.value }))}
                            className={`w-full text-sm border rounded px-2 py-1.5 bg-white ${duplicate ? 'border-amber-400' : 'border-gray-300'}`}
                          >
                            {TARGET_FIELDS.map((f, i) => (
                              f.group
                                ? <option key={`g${i}`} disabled className="text-gray-400">── {f.group} ──</option>
                                : <option key={f.value} value={f.value}>{f.label}{f.hint ? ` · ${f.hint}` : ''}</option>
                            ))}
                          </select>
                          {duplicate && (
                            <div className="text-[10.5px] text-amber-700 mt-1">⚠️ Also mapped from: {usage.filter((u) => u !== col).join(', ')}</div>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-gray-600 text-xs break-all">{samples}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="px-5 py-3 border-t border-gray-200 flex flex-wrap items-center gap-4">
                <label className="inline-flex items-center gap-2 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    checked={updateExisting}
                    onChange={(e) => setUpdateExisting(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Update existing leads when phone or email matches
                </label>
                <div className="ml-auto flex items-center gap-2">
                  <button onClick={reset} className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded">Cancel</button>
                  <button
                    onClick={handleImport}
                    disabled={isImporting || !hasIdentity}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded text-sm font-medium"
                  >
                    {isImporting
                      ? <><Loader2 size={14} className="animate-spin" /> Importing…</>
                      : <><Upload size={14} /> Import {analysis.totalRows} rows</>}
                  </button>
                </div>
              </div>
              {!hasIdentity && (
                <div className="px-5 pb-3 -mt-1 text-[11px] text-amber-700">
                  ⚠️ Map at least one of: Full name / First name / Email / Phone — otherwise rows have nothing to identify them by.
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Step 3 — Results */}
        {step === 'done' && results && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <Stat label="Total rows" value={results.total} />
              <Stat label="Created" value={results.created} color="emerald" />
              <Stat label="Updated" value={results.updated} color="indigo" />
              <Stat label="Skipped" value={results.skipped} color={results.skipped > 0 ? 'amber' : 'gray'} />
            </div>
            {results.errors?.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-200 flex items-center gap-2 text-sm font-medium text-gray-900">
                  <AlertCircle size={14} className="text-amber-600" />
                  {results.errors.length} row{results.errors.length === 1 ? '' : 's'} skipped or failed
                </div>
                <div className="max-h-72 overflow-y-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 text-gray-500 uppercase tracking-wide">
                      <tr>
                        <th className="px-4 py-1.5 text-left">Row</th>
                        <th className="px-4 py-1.5 text-left">Name</th>
                        <th className="px-4 py-1.5 text-left">Reason</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {results.errors.map((e, i) => (
                        <tr key={i}>
                          <td className="px-4 py-1.5 text-gray-600">{e.row}</td>
                          <td className="px-4 py-1.5 text-gray-800">{e.name}</td>
                          <td className="px-4 py-1.5 text-gray-600">{e.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={reset} className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded text-sm font-medium">
                <RefreshCw size={14} /> Import another file
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

const StepDot = ({ active, done, label }) => (
  <div className={`inline-flex items-center gap-1.5 ${active ? 'text-primary-700' : done ? 'text-emerald-700' : 'text-gray-400'}`}>
    <span className={`h-2 w-2 rounded-full ${active ? 'bg-primary-600' : done ? 'bg-emerald-500' : 'bg-gray-300'}`} />
    {label}
  </div>
);

const Stat = ({ label, value, color = 'gray' }) => {
  const tints = {
    gray: 'bg-white text-gray-900 border-gray-200',
    emerald: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    indigo: 'bg-indigo-50 text-indigo-900 border-indigo-200',
    amber: 'bg-amber-50 text-amber-900 border-amber-200',
  };
  return (
    <div className={`rounded-lg border p-3 ${tints[color]}`}>
      <div className="text-[11px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="mt-1 text-xl font-semibold">{value}</div>
    </div>
  );
};

export default CSVImport;
